import React from "react";
import { Button, FormControl, FormLabel, HStack, Input, SimpleGrid, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import RiskReviewErrorState from "../components/common/RiskReviewErrorState";
import RiskReviewLoadingState from "../components/common/RiskReviewLoadingState";
import RiskReviewPageShell from "../components/common/RiskReviewPageShell";
import RiskReviewPanel from "../components/common/RiskReviewPanel";
import RiskReviewTemplateBuilder from "../components/templates/RiskReviewTemplateBuilder";
import { riskReviewKeys } from "../constants/riskReviewQueryKeys";
import { useRiskReviewTemplate } from "../hooks/useRiskReviewTemplates";
import riskReviewService from "../services/riskReviewService";
import { getRiskReviewErrorMessage } from "../utils/riskReviewFormatters";

const latestVersion = (template, versionId) => {
  if (!template) return null;
  if (versionId) return template.versions?.find((version) => version._id === versionId);
  return [...(template.versions || [])].sort((a, b) => b.version - a.version)[0];
};

const RiskReviewTemplateBuilderPage = () => {
  const { id, versionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isCreate = !id;
  const query = useRiskReviewTemplate(id);
  const [templateDraft, setTemplateDraft] = React.useState({
    name: "",
    code: "",
    requestType: "THIRD_PARTY",
    businessUnit: "",
    country: "",
  });
  const [versionDraft, setVersionDraft] = React.useState(null);

  const createMutation = useMutation({
    mutationFn: riskReviewService.createTemplate,
    onSuccess: (template) => {
      toast({ title: "Template created", status: "success" });
      navigate(`/risk-review/templates/${template._id}`);
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const publishMutation = useMutation({
    mutationFn: riskReviewService.publishTemplateVersion,
    onSuccess: () => {
      toast({ title: "Template published", status: "success" });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.template(id) });
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const saveMutation = useMutation({
    mutationFn: riskReviewService.updateTemplateVersion,
    onSuccess: () => {
      toast({ title: "Template draft saved", status: "success" });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.template(id) });
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const template = query.data;
  const version = latestVersion(template, versionId);
  const activeVersion = versionDraft || version;
  const isDraft = version?.status === "DRAFT";

  React.useEffect(() => {
    setVersionDraft(version ? JSON.parse(JSON.stringify(version)) : null);
  }, [version?._id]);

  if (isCreate) {
    return (
      <RiskReviewPageShell title="Create Template" description="Create a Risk Review template draft.">
        <RiskReviewPanel p={5} maxW="960px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {["name", "code", "requestType", "businessUnit", "country"].map((key) => (
              <FormControl key={key}>
                <FormLabel textTransform="capitalize">{key.replace(/([A-Z])/g, " $1")}</FormLabel>
                <Input
                  placeholder={key}
                  value={templateDraft[key]}
                  onChange={(event) => setTemplateDraft((current) => ({ ...current, [key]: event.target.value }))}
                />
              </FormControl>
            ))}
          </SimpleGrid>
          <Button
            mt={5}
            colorScheme="blue"
            isLoading={createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                ...templateDraft,
                versions: [
                  {
                    version: 1,
                    status: "DRAFT",
                    sections: [
                      {
                        code: "REQUEST_CONTEXT",
                        name: "Request Context",
                        description: "Core business context for this Risk Review.",
                        sequence: 1,
                        collapsible: true,
                        defaultExpanded: true,
                        required: true,
                        active: true,
                        fields: [],
                      },
                    ],
                  },
                ],
              })
            }
          >
            Create Template
          </Button>
        </RiskReviewPanel>
      </RiskReviewPageShell>
    );
  }

  if (query.isLoading) {
    return (
      <RiskReviewPageShell title="Risk Review Template">
        <RiskReviewLoadingState />
      </RiskReviewPageShell>
    );
  }

  if (query.isError) {
    return (
      <RiskReviewPageShell title="Risk Review Template">
        <RiskReviewErrorState error={query.error} onRetry={query.refetch} />
      </RiskReviewPageShell>
    );
  }

  return (
    <RiskReviewPageShell
      title={template?.name || "Risk Review Template"}
      description={`${isDraft ? "Draft" : "Read-only"} v${version?.version || "—"}`}
      actions={
        <HStack>
          {isDraft ? (
            <>
              <Button
                variant="outline"
                isLoading={saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({
                    templateId: id,
                    versionId: version._id,
                    payload: {
                      sections: activeVersion.sections || [],
                      approvalRules: activeVersion.approvalRules || [],
                      effectiveFrom: activeVersion.effectiveFrom,
                      effectiveTo: activeVersion.effectiveTo,
                    },
                  })
                }
              >
                Save
              </Button>
              <Button
                colorScheme="green"
                isLoading={publishMutation.isPending}
                onClick={() => publishMutation.mutate({ templateId: id, versionId: version._id })}
              >
                Publish
              </Button>
            </>
          ) : null}
        </HStack>
      }
    >
      <RiskReviewTemplateBuilder
        template={template}
        version={activeVersion}
        isReadOnly={!isDraft}
        onChange={setVersionDraft}
      />
    </RiskReviewPageShell>
  );
};

export default RiskReviewTemplateBuilderPage;
